import type { ReactNode } from 'react';
import { Container, Stack } from '@mantine/core';
import type { ProgramConfig, SubmissionSupportConfig } from '../types/schema';
import ProgramFooter from './ProgramFooter';
import ProgramHeader from './ProgramHeader';
import ProgramSupportInfo from './ProgramSupportInfo';
import SessionUtilityBar from './SessionUtilityBar';

interface PageShellProps {
  config: ProgramConfig;
  children: ReactNode;
  // Header overrides — auth pages supply page-specific logo/title from pages.login config
  headerLogoUrl?: string;
  headerEyebrow?: string;
  headerTitle?: string;
  headerSubtitle?: string;
  headerSubtitleNote?: ReactNode;
  headerSubtitlePadding?: number | string;
  headerTitleOrder?: 1 | 2 | 3 | 4 | 5 | 6;
  headerTitleAlign?: 'left' | 'center';
  // Session controls — omit on pre-auth pages
  sessionEmail?: string;
  onGoHome?: () => void;
  onLogout?: () => void;
  isLoggingOut?: boolean;
  // Layout
  containerSize?: string;
  hideTitle?: boolean;
  showFooter?: boolean;
  contentSurface?: boolean;
  // Support info — caller evaluates showSupportInfo and passes config.support when true
  support?: SubmissionSupportConfig;
}

export default function PageShell({
  config,
  children,
  headerLogoUrl,
  headerEyebrow,
  headerTitle,
  headerSubtitle,
  headerSubtitleNote,
  headerSubtitlePadding,
  headerTitleOrder,
  headerTitleAlign,
  sessionEmail,
  onGoHome,
  onLogout,
  isLoggingOut = false,
  containerSize = 'sm',
  hideTitle = false,
  showFooter = false,
  contentSurface,
  support,
}: PageShellProps) {
  const isLovable = config.theme.uiStyle === 'lovable';
  const hasContentSurface = contentSurface ?? config.theme.contentSurface === true;

  return (
    <Container
      size={containerSize}
      pt="xl"
      pb={isLovable ? 0 : 'xl'}
      className={hasContentSurface ? 'portal-page portal-page--content-surface' : 'portal-page'}
    >
      <Stack gap={hasContentSurface ? 0 : 'lg'}>
        <ProgramHeader
          logoUrl={headerLogoUrl ?? config.theme.logoUrl}
          eyebrow={headerEyebrow}
          programName={headerTitle ?? config.theme.displayName}
          portalName={config.theme.portalName}
          subtitle={headerSubtitle}
          subtitleNote={headerSubtitleNote}
          subtitlePadding={headerSubtitlePadding}
          titleOrder={headerTitleOrder}
          titleAlign={headerTitleAlign ?? config.theme.titleAlign}
          containerSize={containerSize}
          mb={0}
          showTopBar={isLovable}
          showTopBarLogo={config.theme.showTopBarLogo !== false}
          showTopBarPortalName={config.theme.showTopBarPortalName !== false}
          topBarLogoUrl={config.theme.topBarLogoUrl}
          topBarLogoAlt={config.theme.topBarLogoAlt}
          {...(config.theme.topBarLogoWidth != null && { topBarLogoWidth: config.theme.topBarLogoWidth })}
          {...(config.theme.topBarLogoHeight != null && { topBarLogoHeight: config.theme.topBarLogoHeight })}
          hideTitle={hideTitle}
          sessionEmail={isLovable ? sessionEmail : undefined}
          onLogout={isLovable ? onLogout : undefined}
          isLoggingOut={isLovable ? isLoggingOut : false}
        />
        {!isLovable && (sessionEmail || onLogout || onGoHome) && (
          <SessionUtilityBar
            sessionEmail={sessionEmail}
            showHome={Boolean(onGoHome)}
            onGoHome={onGoHome}
            onLogout={onLogout}
            isLoggingOut={isLoggingOut}
          />
        )}
        {hasContentSurface ? <div className="portal-content-surface">{children}</div> : children}
        <ProgramSupportInfo support={support} />
        {isLovable && (config.authenticationRequired || showFooter || config.theme.prescribingInformationUrl) && (
          <ProgramFooter
            visible
            tagline={config.theme.footerTagline}
            legalText={config.theme.footerLegalText}
            prescribingInformationUrl={config.theme.prescribingInformationUrl}
            feedbackUrl={config.theme.feedbackUrl}
            showLogo={config.theme.showFooterLogo !== false}
            logoUrl={config.theme.footerLogoUrl}
          />
        )}
      </Stack>
    </Container>
  );
}
